import {useGrpcRequest} from '@aboutyou/data-fetcher';
import {SizeSelectionService_GetSizeSelectionLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/size_selection/v1/service.grpc.lazy';
import {useBasketToken, useBasketTokenAwaitedAnySession} from '@aboutyou/user';
import {usePrevious} from '@aboutyou/utils';
import {useMemo} from 'react';
import {useActivatedCouponState} from '../features/Coupons/useActivatedCouponState';
import {useReductionsStateRequestParameter} from '../features/ReductionsState/useReductionsStateRequestParameter';
import {useTargetGroupIdentifier} from './useTargetGroupIdentifier';

interface UseGetSizeSelectionConfig {
  productId: number;
  enableSizeFinder: boolean;
  isRequestDisabled?: boolean;
}

export const useGetSizeSelection = ({
  productId,
  enableSizeFinder,
  isRequestDisabled,
}: UseGetSizeSelectionConfig) => {
  const targetGroupIdentifier = useTargetGroupIdentifier();

  const {token: basketToken} = useBasketToken();
  const {session, refetchesDisabled} = useBasketTokenAwaitedAnySession();
  const {activeCouponId, previousActiveCouponId} =
    useGetCurrentAndPreviousActiveCouponIdMemo(productId);
  const hasActiveCouponIdChanged = previousActiveCouponId !== activeCouponId;

  return useGrpcRequest(
    SizeSelectionService_GetSizeSelectionLazy,
    {
      productId,
      session,
      targetGroup: targetGroupIdentifier,
      enableSizeFinder,
      reductionsState: useReductionsStateRequestParameter(),
      selectedCouponPromotionId: activeCouponId ?? null,
      basketToken,
      enableFitmatch: false,
      fitmatchScanningCapableDevice: false,
      disableModelSizeAdvice: null, // unused for now
    },
    {
      disabled: isRequestDisabled || refetchesDisabled,
      staleTime: 0,
      keepPreviousData: hasActiveCouponIdChanged,
    },
  );
};

const useGetCurrentAndPreviousActiveCouponIdMemo = (productId?: number) => {
  const {couponPromotionId: activeCouponId} =
    useActivatedCouponState(productId);
  const previousActiveCouponId = usePrevious(activeCouponId);
  const memoCurrentAndPreviousActiveCouponId = useMemo(
    () => ({
      activeCouponId,
      previousActiveCouponId,
    }),
    [activeCouponId],
  );

  return memoCurrentAndPreviousActiveCouponId;
};
